import { buildTitle } from "@/lib/seo-quality";
import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { getSpotBySlug, addSpotComment } from "@/lib/camping.functions";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/use-auth";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { MapPin, ArrowLeft, MessageCircle } from "lucide-react";
import { ShareButtons } from "@/components/ShareButtons";
import { CampingTipBody } from "@/components/CampingTipBody";
import { SpotFactCard } from "@/components/SpotFactCard";
import { AdSlot } from "@/components/AdSlot";
import { imgSrcSet, HERO_WIDTHS, HALF_SIZES } from "@/lib/img";

export const Route = createFileRoute("/campingtips/$slug")({
  loader: async ({ params }) => {
    const res = await getSpotBySlug({ data: { slug: params.slug } });
    if (!res.spot) throw notFound();
    return res;
  },
  head: ({ loaderData, params }) => {
    const s = loaderData?.spot;
    if (!s) return { meta: [{ title: "Tips hittades inte" }, { name: "robots", content: "noindex" }] };
    const path = `https://www.akhusbilfritid.se/campingtips/${params.slug}`;
    const desc = (s.description ?? "").slice(0, 155).replace(/\s+/g, " ").trim();
    const cover = (s.image_urls ?? []).find((u: string) => u.startsWith("http"));
    return {
      meta: [
        { title: buildTitle(s.title) },
        { name: "description", content: desc },
        { property: "og:type", content: "article" },
        { property: "og:title", content: s.title },
        { property: "og:description", content: desc },
        { property: "og:url", content: path },
        { name: "twitter:card", content: "summary_large_image" },
        ...(cover ? [{ property: "og:image", content: cover }, { name: "twitter:image", content: cover }] : []),
      ],
      links: [{ rel: "canonical", href: path }],
      scripts: [
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristAttraction",
            name: s.title,
            description: desc,
            address: s.region ? { "@type": "PostalAddress", addressRegion: s.region, addressCountry: "SE" } : undefined,
            geo: s.latitude && s.longitude ? { "@type": "GeoCoordinates", latitude: s.latitude, longitude: s.longitude } : undefined,
          }),
        },
        {
          type: "application/ld+json",
          children: JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            itemListElement: [
              { "@type": "ListItem", position: 1, name: "Hem", item: "https://www.akhusbilfritid.se/" },
              { "@type": "ListItem", position: 2, name: "Campingtips", item: "https://www.akhusbilfritid.se/campingtips" },
              { "@type": "ListItem", position: 3, name: s.title, item: path },
            ],
          }),
        },
      ],
    };
  },
  component: SpotPage,
  notFoundComponent: () => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-5xl">Tipset finns <span className="font-italic-display text-accent">inte</span>.</h1>
      <Link to="/campingtips" className="mt-8 inline-flex rounded-full bg-primary px-5 py-3 text-sm text-primary-foreground">Till campingtips</Link>
    </div>
  ),
  errorComponent: ({ error }: { error: unknown }) => (
    <div className="container-edge py-32 text-center">
      <h1 className="font-display text-3xl">Något gick fel</h1>
      <p className="mt-2 text-sm text-muted-foreground">{error instanceof Error ? error.message : String(error)}</p>
    </div>
  ),
});

function SpotPage() {
  const data = Route.useLoaderData();
  const { spot, author, comments } = data;
  const [urls, setUrls] = useState<string[]>([]);
  const { signedIn } = useAuth();
  const qc = useQueryClient();
  const add = useServerFn(addSpotComment);
  const [body, setBody] = useState("");
  const [posting, setPosting] = useState(false);

  useEffect(() => {
    (async () => {
      const resolved = await Promise.all(
        (spot!.image_urls ?? []).map(async (u: string) => {
          if (u.startsWith("http")) return u;
          const { data } = await supabase.storage.from("user-content").createSignedUrl(u, 60 * 60 * 24);
          return data?.signedUrl ?? "";
        }),
      );
      setUrls(resolved.filter(Boolean));
    })();
  }, [spot]);

  const commentsQ = useQuery({
    queryKey: ["spot", spot!.id, "comments"],
    queryFn: async () => {
      const r = await getSpotBySlug({ data: { slug: spot!.slug } });
      return r.comments;
    },
    initialData: comments,
  });

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    if (body.trim().length < 2) return;
    setPosting(true);
    try {
      await add({ data: { spot_id: spot!.id, body: body.trim() } });
      setBody("");
      qc.invalidateQueries({ queryKey: ["spot", spot!.id, "comments"] });
      toast.success("Kommentar publicerad");
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Kunde inte publicera");
    } finally {
      setPosting(false);
    }
  }

  return (
    <article>
      <div className="container-edge max-w-4xl py-12 md:py-20">
        <Link to="/campingtips" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-accent">
          <ArrowLeft size={14} /> Alla tips
        </Link>

        <p className="mt-10 text-xs font-medium uppercase tracking-wider text-accent">
          {spot!.region || "Sverige"} · {spot!.spot_type || "camping"}
        </p>
        <h1 className="mt-3 font-display text-5xl leading-[1.02] md:text-7xl">{spot!.title}</h1>

        {(spot!.author_name || author?.display_name) && (
          <p className="mt-4 text-sm text-muted-foreground">
            {spot!.editorial ? "Av " : "Delat av "}
            <span className="text-foreground">{spot!.author_name || author?.display_name}</span>
          </p>
        )}

        <SpotFactCard
          region={spot!.region}
          spotType={spot!.spot_type}
          latitude={spot!.latitude}
          longitude={spot!.longitude}
          details={spot!}
        />


        {urls.length > 0 && (
          <div className="mt-10 grid gap-3 sm:grid-cols-2">
            {urls.map((u, i) => (
              <img
                key={i}
                src={u}
                srcSet={imgSrcSet(u, HERO_WIDTHS)}
                sizes={HALF_SIZES}
                alt={`${spot!.title} bild ${i + 1}`}
                loading={i === 0 ? "eager" : "lazy"}
                decoding="async"
                onError={(e) => { (e.currentTarget as HTMLImageElement).style.display = "none"; }}
                className="w-full rounded-3xl object-cover elev-shadow"
              />
            ))}
          </div>
        )}
        {spot!.photo_credit && (
          <p className="mt-2 text-[11px] text-muted-foreground">{spot!.photo_credit}</p>
        )}

        <CampingTipBody text={spot!.description ?? ""} />

        <AdSlot variant="in-article" />

        <ShareButtons title={spot!.title} path={`/campingtips/${spot!.slug}`} />

        <AdSlot variant="multiplex" label="Relaterat innehåll" />


        {spot!.latitude != null && spot!.longitude != null && (
          <a
            href={`https://www.google.com/maps?q=${spot!.latitude},${spot!.longitude}`}
            target="_blank" rel="noopener noreferrer"
            className="mt-8 inline-flex items-center gap-2 rounded-full border border-border bg-card px-5 py-3 text-sm font-medium hover:bg-secondary"
          >
            <MapPin size={15} /> Visa på karta ({spot!.latitude.toFixed(4)}, {spot!.longitude.toFixed(4)})
          </a>
        )}

        {/* Kommentarer */}
        <section className="mt-16 border-t border-border pt-10">
          <h2 className="flex items-center gap-2 font-display text-2xl">
            <MessageCircle size={20} /> Kommentarer
          </h2>

          <ul className="mt-6 space-y-4">
            {(commentsQ.data ?? []).length === 0 && (
              <p className="text-sm text-muted-foreground">Inga kommentarer ännu.</p>
            )}
            {(commentsQ.data ?? []).map((c: any) => (
              <li key={c.id} className="rounded-2xl border border-border bg-card p-4">
                <p className="text-xs text-muted-foreground">
                  {c.author?.display_name || "Medlem"} · {new Date(c.created_at).toLocaleDateString("sv-SE")}
                </p>
                <p className="mt-1 whitespace-pre-wrap text-sm text-foreground/90">{c.body}</p>
              </li>
            ))}
          </ul>

          {signedIn ? (
            <form onSubmit={submit} className="mt-6 space-y-3">
              <textarea
                value={body}
                onChange={(e) => setBody(e.target.value)}
                rows={3}
                maxLength={2000}
                placeholder="Din kommentar…"
                className="w-full rounded-2xl border border-input bg-background px-4 py-3 outline-none focus:border-accent focus:ring-2 focus:ring-accent/20"
              />
              <button disabled={posting || body.trim().length < 2} className="rounded-full bg-primary px-5 py-3 text-sm font-medium text-primary-foreground disabled:opacity-60">
                {posting ? "Publicerar…" : "Kommentera"}
              </button>
            </form>
          ) : (
            <p className="mt-6 rounded-2xl border border-border bg-card p-4 text-sm">
              <Link to="/login" className="text-accent hover:underline">Logga in</Link> för att kommentera.
            </p>
          )}
        </section>
      </div>
    </article>
  );
}
